import { supabase } from '../supabase';
import { Database, Tables, TablesInsert, TablesUpdate } from '@/types/database.types';
import { handleError, checkWorkspaceAccess, checkWorkspaceAdmin } from './db-utils';

// Type aliases for cleaner code
type ProjectRow = Tables<'projects'>;
type ProjectInsert = TablesInsert<'projects'>;
type ProjectUpdate = TablesUpdate<'projects'>;
type ProjectStatus = Database['public']['Enums']['project_status'];

interface CreateProjectOptions {
  overallHealth?: 'green' | 'yellow' | 'red';
  startDate?: string | null;
  endDate?: string | null;
  completionPercentage?: number | null;
  externalId?: string;
  status?: ProjectStatus;
}

/**
 * Create a new project
 */
export const createProject = async (
  workspaceId: string,
  name: string,
  description?: string,
  options?: CreateProjectOptions
): Promise<ProjectRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check workspace access
    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) {
      throw new Error('Access denied to workspace');
    }

    const projectData: ProjectInsert = {
      workspace_id: workspaceId,
      name,
      description,
      created_by: user.id,
      status: options?.status ?? 'active',
      overall_health: options?.overallHealth || 'green',
      start_date: options?.startDate || null,
      end_date: options?.endDate || null,
      completion_percentage: options?.completionPercentage ?? null,
      external_id: options?.externalId || null,
    };

    const { data, error } = await supabase
      .from('projects')
      .insert(projectData)
      .select()
      .single();

    if (error) handleError(error, 'create project');
    if (!data) throw new Error('No data returned from create');
    return data;
  } catch (error) {
    handleError(error, 'create project');
    throw error;
  }
};

interface GetWorkspaceProjectsOptions {
  includeDrafts?: boolean;
}

/**
 * Get all projects for a workspace with stats. Drafts are filtered out by
 * default — pass `{ includeDrafts: true }` to include them.
 */
export const getWorkspaceProjects = async (
  workspaceId: string,
  options?: GetWorkspaceProjectsOptions
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check workspace access
    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) {
      throw new Error('Access denied to workspace');
    }

    // Get projects
    let query = supabase
      .from('projects')
      .select('*')
      .eq('workspace_id', workspaceId);

    if (!options?.includeDrafts) {
      query = query.neq('status', 'draft');
    }

    const { data: projects, error: projectsError } = await query
      .order('created_at', { ascending: false });

    if (projectsError) handleError(projectsError, 'fetch workspace projects');
    if (!projects) return [];

    // Get milestones and KPIs for each project
    const projectsWithStats = await Promise.all(
      projects.map(async (project) => {
        // Get milestones
        const { data: milestones } = await supabase
          .from('milestones')
          .select('*')
          .eq('project_id', project.id)
          .order('due_date', { ascending: true });

        // Get next milestone
        const nextMilestone = milestones?.find(m => m.status !== 'completed');

        // Calculate progress based on completed milestones
        const totalMilestones = milestones?.length || 0;
        const completedMilestones = milestones?.filter(m => m.status === 'completed').length || 0;
        const progress = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;

        // Get team member count (for now, just count workspace users)
        // In a real app, you might have project-specific members
        const { count: teamMembers } = await supabase
          .from('workspace_users')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId);

        return {
          ...project,
          progress,
          nextMilestone: nextMilestone?.name || null,
          nextMilestoneDue: nextMilestone?.due_date || null,
          totalMilestones,
          completedMilestones,
          teamMembers: teamMembers || 0
        };
      })
    );

    return projectsWithStats;
  } catch (error) {
    handleError(error, 'fetch workspace projects');
    return [];
  }
};

/**
 * Get the current user's draft projects in a workspace. RLS already enforces
 * creator-only visibility for drafts, but the explicit `created_by` filter
 * makes the intent unambiguous at the call site.
 */
export const getUserDraftsInWorkspace = async (
  workspaceId: string
): Promise<ProjectRow[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) {
      throw new Error('Access denied to workspace');
    }

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('status', 'draft')
      .eq('created_by', user.id)
      .order('created_at', { ascending: false });

    if (error) handleError(error, 'fetch user drafts');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch user drafts');
    return [];
  }
};

/**
 * Get project by ID
 */
export const getProjectById = async (projectId: string): Promise<ProjectRow | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        workspaces!inner(*)
      `)
      .eq('id', projectId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      handleError(error, 'fetch project');
    }

    if (!data) return null;

    // Check if user has access to the workspace
    const hasAccess = await checkWorkspaceAccess(data.workspace_id);
    if (!hasAccess) {
      throw new Error('Access denied to project');
    }

    return data;
  } catch (error) {
    handleError(error, 'fetch project');
    return null;
  }
};

/**
 * Update project. Includes a defence-in-depth application-layer guard that
 * blocks active → draft demotion: if the caller passes `status: 'draft'`,
 * the current row's status must already be 'draft', otherwise we throw with
 * a clean error before hitting Postgres. The primary security boundary is
 * still the column-level REVOKE on `created_by` (see migration 20260429135621),
 * but this guard makes the intent explicit and surfaces a friendly error in
 * the UI when an in-app caller mistakenly sets `status='draft'` on an active
 * project.
 */
export const updateProject = async (
  projectId: string,
  updates: ProjectUpdate
): Promise<ProjectRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First get the project to check workspace access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    if (updates.status === 'draft' && project.status !== 'draft') {
      throw new Error('Cannot demote an active project to draft');
    }

    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select()
      .single();

    if (error) handleError(error, 'update project');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'update project');
    throw error;
  }
};

/**
 * Promote a draft project to active. Thin wrapper around `updateProject` for
 * call-site clarity — used by the wizard's "Create Project" button and the
 * ProjectDetails "Mark as Ready" button.
 */
export const promoteProjectToActive = async (projectId: string): Promise<ProjectRow> => {
  return updateProject(projectId, { status: 'active' });
};

/**
 * Delete a project. Workspace admins can delete any project; in addition,
 * the original creator of a draft can delete their own draft (so the wizard's
 * "Discard" action and the ProjectDetails "Discard" banner work for non-admin
 * creators). Storage objects under `project-documents/{projectId}/...` are
 * removed before the row delete; the DB cascade handles related rows
 * (documents, milestones, risks, status_updates).
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First get the project to check workspace access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const isOwnDraft = project.status === 'draft' && project.created_by === user.id;
    if (!isOwnDraft) {
      // Non-draft (or someone else's draft) requires admin
      const isAdmin = await checkWorkspaceAdmin(project.workspace_id);
      if (!isAdmin) {
        throw new Error('Only workspace admins can delete projects');
      }
    }

    // Clean up storage objects under project-documents/{projectId}/... before
    // the row delete. The DB cascade removes the document/milestone/risk/etc.
    // rows but NOT the storage blobs, so without this we'd leak storage.
    //
    // Page through `documents` so projects with >1000 docs aren't truncated by
    // the default Supabase row cap. Fail-fast on storage error: if any batch
    // fails, abort before the row delete so we don't orphan blobs. Storage
    // outages are rare; the UX cost (Discard temporarily blocked) is preferred
    // over silent leaks.
    const PAGE = 1000;
    const allPaths: string[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data: docs, error: docsError } = await supabase
        .from('documents')
        .select('file_path')
        .eq('project_id', projectId)
        .range(from, from + PAGE - 1);

      if (docsError) handleError(docsError, 'list project documents for storage cleanup');
      if (!docs || docs.length === 0) break;

      for (const d of docs) {
        if (d.file_path) allPaths.push(d.file_path);
      }

      if (docs.length < PAGE) break;
    }

    if (allPaths.length > 0) {
      // Supabase storage `remove` accepts an array of paths; chunk to the same
      // page size to keep payloads bounded.
      for (let i = 0; i < allPaths.length; i += PAGE) {
        const chunk = allPaths.slice(i, i + PAGE);
        const { error: removeError } = await supabase.storage
          .from('project-documents')
          .remove(chunk);
        if (removeError) handleError(removeError, 'remove project storage objects');
      }
    }

    // Delete the project - cascade handles documents/milestones/risks/status_updates rows
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) handleError(error, 'delete project');
  } catch (error) {
    handleError(error, 'delete project');
    throw error;
  }
};

// ============================================================================
// FULL PROJECT DATA
// ============================================================================

export interface ProjectWithDetails extends ProjectRow {
  milestones: Tables<'milestones'>[];
  risks: Tables<'risks'>[];
  kpis: Tables<'kpis'>[];
  statusUpdates: Tables<'status_updates'>[];
}

/**
 * Get project with all related data (milestones, risks, status updates)
 */
export const getProjectWithDetails = async (projectId: string): Promise<ProjectWithDetails | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get the project first
    const project = await getProjectById(projectId);
    if (!project) return null;

    // Fetch all related data in parallel
    const [milestonesResult, risksResult, kpisResult, statusUpdatesResult] = await Promise.all([
      supabase
        .from('milestones')
        .select('*')
        .eq('project_id', projectId)
        .order('due_date', { ascending: true, nullsFirst: false }),
      supabase
        .from('risks')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),
      supabase
        .from('kpis')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true }),
      supabase
        .from('status_updates')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),
    ]);

    return {
      ...project,
      milestones: milestonesResult.data || [],
      risks: risksResult.data || [],
      kpis: kpisResult.data || [],
      statusUpdates: statusUpdatesResult.data || [],
    };
  } catch (error) {
    handleError(error, 'fetch project with details');
    return null;
  }
};
